import React from 'react';

/*
  Generic DataTable component with pagination.
  Props:
    - columns: Array<{
        key: string,
        header: string,
        width?: string | number | { base?: string | number, sm?: string | number, md?: string | number, lg?: string | number, xl?: string | number },
        className?: string,
        tdClassName?: string,
        render?: (row, rowIndex) => ReactNode,
        groupRender?: (row, rowIndex, groupSize) => ReactNode // render for first row in group
      }>
    - data: Array<any>
    - loading: boolean
    - page: current page (1-based)
    - pageSize: items per page
    - totalItems: total number of items across all pages
    - onPageChange: (page) => void
    - onPageSizeChange: (newSize) => void
    - pageSizeOptions?: number[]
    - emptyMessage?: string
    - groupBy?: string // field name to group by (e.g., 'code', 'billCode')

  Width Examples:
    width: 100 // fixed 100px
    width: '200px' // fixed 200px
    width: '30%' // fixed 30%
    width: { base: 80, md: 120, lg: 150 } // responsive: 80px on mobile, 120px on md, 150px on lg+
    width: { base: '100px', md: '150px', xl: '200px' } // responsive with string values
*/

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  page = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  emptyMessage = 'ไม่พบข้อมูล',
  showPagination = true,
  groupBy = null,
  groupHeaderAction = null, // function that returns action button for group header
  onGroupHeaderAction = null, // callback for group header action
  paginationUnitLabel = ''
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const computedStartIndex = (safePage - 1) * pageSize;

  // Group data if groupBy is specified
  const processedData = React.useMemo(() => {
    if (!groupBy || !data.length) {
      return data.map((row, index) => ({ row, isFirstInGroup: false, groupSize: 0 }));
    }

    const result = [];
    let currentGroup = null;
    let groupSize = 0;

    data.forEach((row, index) => {
      const groupValue = row[groupBy];

      if (currentGroup !== groupValue) {
        // New group starts
        currentGroup = groupValue;
        groupSize = data.filter(r => r[groupBy] === groupValue).length;
        result.push({ row, isFirstInGroup: true, groupSize });
      } else {
        result.push({ row, isFirstInGroup: false, groupSize: 0 });
      }
    });

    return result;
  }, [data, groupBy]);

  // Helper function to generate responsive width styles
  const getResponsiveWidthStyle = (width) => {
    if (!width) return undefined;

    // If width is simple string or number
    if (typeof width === 'string' || typeof width === 'number') {
      return { width: typeof width === 'number' ? `${width}px` : width };
    }

    // For responsive width object, we'll use CSS custom properties and media queries
    // This will be handled via inline style with calc and clamp
    if (typeof width === 'object') {
      const base = width.base ? (typeof width.base === 'number' ? `${width.base}px` : width.base) : 'auto';
      return { width: base };
    }

    return undefined;
  };

  // Generate style tag for responsive widths
  const generateResponsiveStyles = () => {
    const styles = [];

    columns.forEach((col, index) => {
      if (typeof col.width === 'object' && col.width) {
        const classBase = `col-${col.key}-${index}`;
        let css = `.${classBase} { `;

        if (col.width.base) {
          const val = typeof col.width.base === 'number' ? `${col.width.base}px` : col.width.base;
          css += `width: ${val}; `;
        }

        css += '} ';

        if (col.width.sm) {
          const val = typeof col.width.sm === 'number' ? `${col.width.sm}px` : col.width.sm;
          css += `@media (min-width: 640px) { .${classBase} { width: ${val}; } } `;
        }

        if (col.width.md) {
          const val = typeof col.width.md === 'number' ? `${col.width.md}px` : col.width.md;
          css += `@media (min-width: 768px) { .${classBase} { width: ${val}; } } `;
        }

        if (col.width.lg) {
          const val = typeof col.width.lg === 'number' ? `${col.width.lg}px` : col.width.lg;
          css += `@media (min-width: 1024px) { .${classBase} { width: ${val}; } } `;
        }

        if (col.width.xl) {
          const val = typeof col.width.xl === 'number' ? `${col.width.xl}px` : col.width.xl;
          css += `@media (min-width: 1280px) { .${classBase} { width: ${val}; } } `;
        }

        styles.push(css);
      }
    });

    return styles.length > 0 ? <style>{styles.join(' ')}</style> : null;
  };

  // Get responsive class name for column
  const getResponsiveClassName = (col, index) => {
    if (typeof col.width === 'object' && col.width) {
      return `col-${col.key}-${index}`;
    }
    return '';
  };

  return (
    <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden transition-colors">
      {generateResponsiveStyles()}
      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-300">กำลังโหลดข้อมูล...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[920px]">
              <thead>
                <tr className="bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90">
                  {columns.map((col, inx) => (
                    <th
                      key={col.key}
                      className={`${ inx === 0 ? 'text-left min-w-20' : 'text-center min-w-40' } font-semibold p-4 border-b border-borderc-light dark:border-borderc-dark ${col.className || ''} ${getResponsiveClassName(col, inx)}`}
                      style={getResponsiveWidthStyle(col.width)}
                    >{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="p-6 text-center text-gray-500 dark:text-gray-400"
                    >{emptyMessage}</td>
                  </tr>
                )}
                {processedData.map(({ row, isFirstInGroup, groupSize }, idx) => (
                  <React.Fragment key={row.id || idx}>
                    {isFirstInGroup && groupBy && (
                      <tr className="bg-blue-50 dark:bg-blue-900/20 border-t-2 border-blue-400 dark:border-blue-600">
                        <td colSpan={columns.length} className="px-4 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="font-semibold text-blue-700 dark:text-blue-300">
                                เลขที่คำสั่งซื้อ: {row[groupBy] || row.billCode || row.code}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                ({groupSize} รายการ)
                              </span>
                              {row.group_text ? <span className="text-gray-600 dark:text-gray-400">
                                {row.group_text}
                              </span> : null}
                            </div>
                            {groupHeaderAction && groupHeaderAction(row, groupSize)}
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className={`hover:bg-neutral-50 dark:hover:bg-[#333333] transition-colors`}>
                      {columns.map((col, colIndex) => (
                        <td
                          key={col.key}
                          className={`p-4 border-b border-borderc-light dark:border-borderc-dark text-gray-700 dark:text-white/70 ${col.tdClassName || ''} ${getResponsiveClassName(col, colIndex)}`}
                          style={getResponsiveWidthStyle(col.width)}
                        >
                          {isFirstInGroup && col.groupRender
                            ? col.groupRender(row, idx, groupSize)
                            : col.render
                              ? col.render(row, idx)
                              : row[col.key]
                          }
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {showPagination && (
            <TablePagination
              page={safePage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              startIndex={computedStartIndex}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              unitLabel={paginationUnitLabel}
            />
          )}
        </>
      )}
    </div>
  );
};const TablePagination = ({
  page, totalPages, totalItems, pageSize, pageSizeOptions, onPageChange, onPageSizeChange, unitLabel = ''
}) => {
  const computedStartIndex = (page - 1) * pageSize;

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-t border-borderc-light dark:border-borderc-dark">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={pageSize}
          onChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={() => onPageChange(1)} disabled={page === 1} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">« หน้าแรก</button>
        <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">‹ ก่อนหน้า</button>
        {renderPageButtons(page, totalPages, onPageChange)}
        <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">ถัดไป ›</button>
        <button onClick={() => onPageChange(totalPages)} disabled={page === totalPages} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">สุดท้าย »</button>
      </div>
      <div className="text-xs md:text-sm text-gray-600 dark:text-white/70">
        แสดง{unitLabel ? ` ${unitLabel}` : ''} {totalItems === 0 ? 0 : computedStartIndex + 1} ถึง {Math.min(computedStartIndex + pageSize, totalItems)} จาก {totalItems}
      </div>
    </div>
  );
};

function renderPageButtons(current, total, goto) {
  const buttons = [];
  const addBtn = (p, label = p, active = false) => {
    buttons.push(
      <button
        key={p + '-' + label}
        onClick={() => goto(p)}
        disabled={active}
        className={`min-w-[36px] px-2 py-1 border rounded transition text-sm ${active ? 'bg-blue-500 border-blue-500 text-white font-semibold' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
      >{label}</button>
    );
  };
  const addEllipsis = (key) => buttons.push(<span key={key} className="px-1 text-gray-400 dark:text-black/50">…</span>);

  if (total <= 9) {
    for (let p = 1; p <= total; p++) addBtn(p, p, p === current);
  } else {
    addBtn(1, 1, current === 1);
    if (current > 4) addEllipsis('start');
    const start = Math.max(2, current - 2);
    const end = Math.min(total - 1, current + 2);
    for (let p = start; p <= end; p++) addBtn(p, p, p === current);
    if (current < total - 3) addEllipsis('end');
    addBtn(total, total, current === total);
  }
  return buttons;
}

export default DataTable;
